import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const ServiceCard = ({ icon, title, description, features, onCotizar, formType }) => {
  return (
    <Card className="card-hover group bg-card border-0 overflow-hidden h-full">
      <CardContent className="p-6 flex flex-col h-full">
        <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
          <i className={`fas ${icon} text-2xl text-primary group-hover:text-white transition-colors duration-300`}></i>
        </div>
        
        <h3 className="text-xl font-bold text-foreground mb-2">{title}</h3>
        <p className="text-muted-foreground text-sm mb-4 leading-relaxed flex-grow">{description}</p>
        
        <ul className="space-y-2 mb-5">
          {features.map((feature, idx) => (
            <li key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
              <i className="fas fa-check text-primary text-xs"></i>
              {feature}
            </li>
          ))}
        </ul>
        
        <Button 
          onClick={() => onCotizar(formType)}
          variant="outline" 
          className="w-full mt-auto border-primary text-primary hover:bg-primary hover:text-white rounded-full transition-all duration-300"
        >
          Pedir Cotización
        </Button>
      </CardContent>
    </Card>
  );
};

export const Services = ({ onCotizarClick }) => {
  const [activeTab, setActiveTab] = useState('individuos');

  return (
    <section id="servicios" className="py-24 bg-secondary/30">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
            <i className="fas fa-shield-alt text-primary"></i>
            <span className="text-sm font-medium text-primary">Nuestros Servicios</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Soluciones de seguros para{' '}
            <span className="text-primary">cada necesidad</span>
          </h2>
          
          <p className="text-muted-foreground text-lg">
            Ofrecemos una amplia gama de seguros y servicios de gestoría para proteger lo que más te importa.
          </p>
        </div>

        <div className="flex justify-center mb-12">
          <div className="bg-muted p-1.5 rounded-full inline-flex">
            <button 
              onClick={() => setActiveTab('individuos')}
              className={`px-8 py-3 rounded-full text-base font-semibold transition-all duration-300 ${
                activeTab === 'individuos' 
                  ? 'bg-primary text-white shadow-lg' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <i className="fas fa-user mr-2"></i>
              Individuos
            </button>
            <button 
              onClick={() => setActiveTab('empresas')}
              className={`px-8 py-3 rounded-full text-base font-semibold transition-all duration-300 ${
                activeTab === 'empresas' 
                  ? 'bg-primary text-white shadow-lg' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <i className="fas fa-building mr-2"></i>
              Empresas
            </button>
          </div>
        </div>

        {activeTab === 'individuos' && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <ServiceCard 
              icon="fa-car"
              title="Seguro de Auto"
              description="Protección integral para tu vehículo con cobertura contra robo, accidentes y responsabilidad civil."
              features={['Amplitud de coberturas', 'Grúa incluida', 'Descuentos exclusivos']}
              onCotizar={onCotizarClick}
              formType="individual"
            />
            <ServiceCard 
              icon="fa-home"
              title="Seguro del Hogar"
              description="Protege tu hogar y pertenencias contra incendios, robos, daños por agua y más."
              features={['Incendio y robo', 'Responsabilidad civil', 'Cristales']}
              onCotizar={onCotizarClick}
              formType="individual"
            />
            <ServiceCard 
              icon="fa-paw"
              title="Seguro para Mascotas"
              description="Cobertura veterinaria para tu compañero. Incluye consultas, cirugías y medicamentos."
              features={['Consultas veterinarias', 'Cirugías', 'Vacunas incluidas']}
              onCotizar={onCotizarClick}
              formType="individual"
            />
            <ServiceCard 
              icon="fa-plane"
              title="Asistencia al Viajero"
              description="Viaja tranquilo con cobertura médica internacional, equipaje y cancelación de vuelos."
              features={['Cobertura médica', 'Equipaje perdido', 'Cancelaciones']}
              onCotizar={onCotizarClick}
              formType="individual"
            />
            <ServiceCard 
              icon="fa-store"
              title="Integral de Comercio"
              description="Protección completa para tu negocio. Cubre incendio, robo, responsabilidad civil y más."
              features={['Incendio y robo', 'Responsabilidad civil', 'Cristales y carteles']}
              onCotizar={onCotizarClick}
              formType="individual"
            />
            <ServiceCard 
              icon="fa-motorcycle"
              title="Monopatines Eléctricos"
              description="Seguro especializado para tu monopatín eléctrico con responsabilidad civil."
              features={['Responsabilidad civil', 'Robo total']}
              onCotizar={onCotizarClick}
              formType="individual"
            />
          </div>
        )}

        {activeTab === 'empresas' && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <ServiceCard 
              icon="fa-building"
              title="Seguros Corporativos"
              description="Soluciones integrales de seguros para proteger los activos de tu empresa."
              features={['Todo riesgo operativo', 'Responsabilidad civil', 'Accidentes personales']}
              onCotizar={onCotizarClick}
              formType="empresa"
            />
            <ServiceCard 
              icon="fa-truck"
              title="Flota de Vehículos"
              description="Cobertura para flotas comerciales con beneficios exclusivos y precios competitivos."
              features={['Múltiples vehículos', 'Descuentos por flota', 'Gestión centralizada']}
              onCotizar={onCotizarClick}
              formType="empresa"
            />
            <ServiceCard 
              icon="fa-users-cog"
              title="ART - Riesgos del Trabajo"
              description="Protección para tus empleados contra accidentes laborales y enfermedades profesionales."
              features={['Cobertura completa', 'Prevención', 'Capacitación']}
              onCotizar={onCotizarClick}
              formType="empresa"
            />
            <ServiceCard 
              icon="fa-fire-extinguisher"
              title="Incendio Comercial"
              description="Protección contra incendios para locales comerciales, oficinas e industrias."
              features={['Contenido', 'Lucro cesante', 'Remoción de escombros']}
              onCotizar={onCotizarClick}
              formType="empresa"
            />
            <ServiceCard 
              icon="fa-hand-holding-usd"
              title="Caución"
              description="Garantías para licitaciones, cumplimiento de contrato y anticipos financieros."
              features={['Licitaciones', 'Contratos', 'Garantías de alquiler']}
              onCotizar={onCotizarClick}
              formType="empresa"
            />
            <ServiceCard 
              icon="fa-shipping-fast"
              title="Transporte de Mercadería"
              description="Cobertura para mercaderías en tránsito nacional e internacional."
              features={['Terrestre', 'Marítimo', 'Aéreo']}
              onCotizar={onCotizarClick}
              formType="empresa"
            />
          </div>
        )}

        <div className="mt-20 relative overflow-hidden rounded-3xl bg-primary p-8 md:p-12">
          <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
            <svg viewBox="0 0 200 200" className="w-full h-full">
              <circle cx="100" cy="100" r="80" fill="white" />
            </svg>
          </div>
          
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-white text-center md:text-left">
              <h3 className="text-2xl md:text-3xl font-bold mb-2">
                Potenciá tu empresa con seguridad
              </h3>
              <p className="text-white/80 text-lg max-w-xl">
                Contamos con soluciones personalizadas para proteger los activos de tu negocio y a tu equipo de trabajo.
              </p>
            </div>
            
            <Button 
              onClick={() => onCotizarClick('empresa')}
              size="lg"
              className="bg-white text-primary hover:bg-white/90 font-bold px-8 py-6 rounded-full shadow-xl whitespace-nowrap"
            >
              <i className="fas fa-briefcase mr-2"></i>
              Asesoría Empresarial
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
